import { jsPDF } from "jspdf";

/** Supported print sheet sizes. */
export const SHEET_SIZES = [
  { id: "a4", label: "A4", subtitle: "21 × 29.7 cm", w: 21.0, h: 29.7 },
  { id: "4x6", label: "4 × 6 in", subtitle: "10.2 × 15.2 cm", w: 10.16, h: 15.24 },
];

export const getSheet = (id) =>
  SHEET_SIZES.find((s) => s.id === id) || SHEET_SIZES[0];

const MARGIN_CM = 0.6;
const GAP_CM = 0.4;
// Small sheets get tighter margins so a 4×6 print still holds a useful grid.
const SMALL_MARGIN_CM = 0.3;
const SMALL_GAP_CM = 0.25;

const spacingFor = (sheet) =>
  sheet.w < 15
    ? { margin: SMALL_MARGIN_CM, gap: SMALL_GAP_CM }
    : { margin: MARGIN_CM, gap: GAP_CM };

/** How many photos of a given size fit on a sheet, and in what grid. */
export function sheetCapacity({ sheet, photoSizeCm, photosPerRow }) {
  const { margin, gap } = spacingFor(sheet);
  const cols = Math.max(
    1,
    Math.min(
      photosPerRow,
      Math.floor((sheet.w - 2 * margin + gap) / (photoSizeCm.w + gap)),
    ),
  );
  const rows = Math.max(
    0,
    Math.floor((sheet.h - 2 * margin + gap) / (photoSizeCm.h + gap)),
  );
  return { cols, rows, capacity: cols * rows };
}

/**
 * Lays each person out as their own block of rows so copies never mix
 * between people. Returns what fits on a single sheet.
 */
export function planSheet({ sheet, photoSizeCm, photosPerRow, people }) {
  const { cols, rows } = sheetCapacity({ sheet, photoSizeCm, photosPerRow });
  const blocks = [];
  let row = 0;
  let placedTotal = 0;
  let requested = 0;

  people.forEach((p, index) => {
    const want = Math.max(1, Number(p.copies) || 1);
    requested += want;
    const rowsAvailable = Math.max(0, rows - row);
    const placed = Math.min(want, rowsAvailable * cols);
    const rowsUsed = Math.ceil(placed / cols);
    blocks.push({ index, startRow: row, placed, requested: want, rowsUsed });
    row += rowsUsed;
    placedTotal += placed;
  });

  return {
    cols,
    rows,
    capacity: cols * rows,
    blocks,
    placedTotal,
    requested,
    overflow: requested - placedTotal,
  };
}

/**
 * Renders one print sheet at 300 DPI. `people` is an ordered list of
 * { dataUrl, copies, name, date, idNo } — each person keeps their own photo
 * and their own labels.
 */
export async function renderPassportSheetCanvas({
  people,
  photoSizeCm,
  photosPerRow,
  dpi = 300,
  sheetId = "a4",
}) {
  const sheet = getSheet(sheetId);
  const plan = planSheet({ sheet, photoSizeCm, photosPerRow, people });
  const { margin, gap } = spacingFor(sheet);
  const cmToPx = (cm) => Math.round((cm / 2.54) * dpi);

  const canvas = document.createElement("canvas");
  canvas.width = cmToPx(sheet.w);
  canvas.height = cmToPx(sheet.h);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const photoWpx = cmToPx(photoSizeCm.w);
  const photoHpx = cmToPx(photoSizeCm.h);
  const gapPx = cmToPx(gap);
  const marginPx = cmToPx(margin);

  const images = await Promise.all(people.map((p) => loadImage(p.dataUrl)));

  for (const block of plan.blocks) {
    const person = people[block.index];
    const img = images[block.index];
    if (!img || block.placed === 0) continue;

    if (block.startRow > 0) {
      const y = marginPx + block.startRow * (photoHpx + gapPx) - gapPx / 2;
      ctx.save();
      ctx.strokeStyle = "#cb05a5";
      ctx.lineWidth = Math.max(2, Math.round(dpi / 100));
      ctx.setLineDash([Math.round(dpi / 6), Math.round(dpi / 14)]);
      ctx.beginPath();
      ctx.moveTo(marginPx, y);
      ctx.lineTo(canvas.width - marginPx, y);
      ctx.stroke();
      ctx.restore();
    }

    for (let i = 0; i < block.placed; i++) {
      const r = block.startRow + Math.floor(i / plan.cols);
      const c = i % plan.cols;
      const x = marginPx + c * (photoWpx + gapPx);
      const y = marginPx + r * (photoHpx + gapPx);

      ctx.drawImage(img, x, y, photoWpx, photoHpx);
      drawPhotoLabel(ctx, {
        x,
        y,
        w: photoWpx,
        h: photoHpx,
        lines: labelLines(person),
      });

      // dashed cutting guide (drawn last so it sits on top)
      ctx.save();
      ctx.setLineDash([Math.round(dpi / 32), Math.round(dpi / 45)]);
      ctx.strokeStyle = "rgba(15, 23, 42, 0.55)";
      ctx.lineWidth = Math.max(1, Math.round(dpi / 300));
      ctx.strokeRect(x, y, photoWpx, photoHpx);
      ctx.restore();
    }
  }

  return { canvas, plan, sheet };
}

export const labelLines = (person) =>
  [
    { text: (person.name || "").trim().toUpperCase(), weight: 800 },
    { text: (person.date || "").trim(), weight: 800 },
    { text: (person.idNo || "").trim(), weight: 800 },
  ].filter((l) => l.text);

// Solid white overlay strip across the bottom of the photo with bold black text:
// NAME (uppercase), DOB, ID — only the lines that were filled in.
function drawPhotoLabel(ctx, { x, y, w, h, lines }) {
  if (!lines.length) return;

  const bannerH = Math.round(h * (0.02 + 0.055 * lines.length));
  const bannerY = y + h - bannerH;

  ctx.save();
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, bannerY, w, bannerH);
  ctx.restore();

  const lineH = bannerH / lines.length;
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#000000";

  lines.forEach((line, i) => {
    let size = Math.round(lineH * 0.78);
    const maxWidth = w * 0.94;
    const font = (s) =>
      `${line.weight} ${s}px "Hanken Grotesk", "Manrope", "Helvetica Neue", Arial, sans-serif`;
    ctx.font = font(size);
    while (ctx.measureText(line.text).width > maxWidth && size > 6) {
      size -= 1;
      ctx.font = font(size);
    }
    ctx.fillText(line.text, x + w / 2, bannerY + lineH * (i + 0.5));
  });
  ctx.restore();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function downloadPassportSheetPdf(canvas, filename, sheetId = "a4") {
  const sheet = getSheet(sheetId);
  const imgData = canvas.toDataURL("image/jpeg", 0.98);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "cm",
    format: [sheet.w, sheet.h],
    compress: true,
  });
  pdf.addImage(imgData, "JPEG", 0, 0, sheet.w, sheet.h, undefined, "FAST");
  pdf.save(filename);
}

export function downloadPassportSheetPng(canvas, filename) {
  canvas.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, "image/png");
}
